/**
 * ERPScreenRenderer.jsx
 *
 * Dynamically renders any ERP screen by its ComponentPath string.
 *
 * Usage:
 *   <ERPScreenRenderer componentPath="pages/Dashboard.jsx" screenProps={{ clientId: '...' }} />
 *
 * How it works:
 *   1. resolveComponent() looks up the ComponentPath in the webpack registry
 *   2. React.lazy() wraps the module factory so it loads on demand (code-split)
 *   3. <Suspense> shows a loading indicator while the chunk is fetched
 *
 * For screens from EXTERNAL ERP projects (not built into this app):
 *   ComponentPath will not be in the registry → "Not in registry" placeholder is shown.
 *   Those screens are rendered inside the ERP app's own build, not here.
 */

import React, { Suspense, lazy, useMemo } from 'react';
import { AlertTriangle, RefreshCw, Code2, ExternalLink } from 'lucide-react';
import { resolveComponent } from './ComponentRegistry';

/* ── Loading placeholder ─────────────────────────────────────────────────── */
function ScreenLoader({ componentPath }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 200, gap: 10, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
      <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
      Loading <code style={{ fontSize: '0.72rem' }}>{componentPath}</code>…
    </div>
  );
}

/* ── Error placeholders ──────────────────────────────────────────────────── */
function NotMapped() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 200, gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center', padding: 24 }}>
      <Code2 size={22} color="#94a3b8" />
      <strong style={{ color: 'var(--text-secondary)' }}>No component mapped</strong>
      <span style={{ fontSize: '0.75rem' }}>
        Go to ERP Workspace → Component Map to assign a component to this screen.
      </span>
    </div>
  );
}

function NotInRegistry({ componentPath }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 200, gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center', padding: 24 }}>
      <ExternalLink size={22} color="#f59e0b" />
      <strong style={{ color: '#f59e0b' }}>External component</strong>
      <code style={{ fontSize: '0.72rem', background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 4 }}>
        {componentPath}
      </code>
      <span style={{ fontSize: '0.75rem', maxWidth: 320, lineHeight: 1.5 }}>
        This component belongs to an external ERP project and is not bundled into this app.
        It will render correctly inside the ERP application's own build.
      </span>
    </div>
  );
}

/* ── Error boundary ──────────────────────────────────────────────────────── */
class ScreenErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: 200, gap: 8, color: '#ef4444', fontSize: '0.82rem', textAlign: 'center', padding: 24 }}>
          <AlertTriangle size={22} />
          <strong>Component error</strong>
          <code style={{ fontSize: '0.7rem', color: 'var(--text-muted)', maxWidth: 400, wordBreak: 'break-all' }}>
            {this.state.error.message}
          </code>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Main renderer ───────────────────────────────────────────────────────── */
/**
 * @param {string}  componentPath  Path stored in ERPScreenSources.ComponentPath
 * @param {object}  screenProps    Props forwarded to the rendered component
 * @param {React.ReactNode} [fallback]  Optional custom "not mapped" placeholder
 */
export function ERPScreenRenderer({ componentPath, screenProps = {}, fallback }) {
  // Resolve ONCE per componentPath change — don't recreate the lazy component on every render
  const LazyScreen = useMemo(() => {
    if (!componentPath) return null;
    const factory = resolveComponent(componentPath);
    if (!factory) return false;  // false = "known to be external"
    // Wrap factory: React.lazy expects () => Promise<{ default: Component }>
    return lazy(() => factory().then(m => ({ default: m.default || m })));
  // eslint-disable-next-line
  }, [componentPath]);

  if (!componentPath)    return fallback || <NotMapped />;
  if (LazyScreen === false) return <NotInRegistry componentPath={componentPath} />;

  return (
    <ScreenErrorBoundary key={componentPath}>
      <Suspense fallback={<ScreenLoader componentPath={componentPath} />}>
        <LazyScreen {...screenProps} />
      </Suspense>
    </ScreenErrorBoundary>
  );
}

export default ERPScreenRenderer;
